import React, { useState } from 'react';
import axios from 'axios';
import { loadCookiePrefs, saveCookiePrefs, LobbyPreferencesData } from './lobbyPreferences';
import styles from './Preferences.module.css';

type PreferencesData = LobbyPreferencesData;

interface PreferencesProps {
  lobbyId: number;
  mercenaryId: number;
  battleType: string;
  preferences: PreferencesData;
  onUpdate: (prefs: PreferencesData) => void;
}

const Preferences: React.FC<PreferencesProps> = ({ lobbyId, mercenaryId, battleType, preferences: initial }) => {
  // Convert fractional (0-1) stored value to integer percentage for sliders
  const toSliderVal = (v: number) => Math.round((v <= 1 ? v * 100 : v) / 10) * 10;

  // Initialise from cookie first, fall back to server-provided values
  const cookied = loadCookiePrefs(mercenaryId, battleType);
  const [surrenderHP, setSurrenderHP] = useState(
    toSliderVal(cookied?.surrenderHP ?? initial.surrenderHP)
  );
  const [readyHP, setReadyHP] = useState(
    toSliderVal(cookied?.readyHP ?? initial.readyHP)
  );

  const handleSurrenderChange = (val: number) => {
    setSurrenderHP(val);
    if (readyHP <= val) setReadyHP(Math.min(100, val + 10));
  };

  const handleReadyChange = (val: number) => {
    setReadyHP(val);
    if (surrenderHP >= val) setSurrenderHP(Math.max(0, val - 10));
  };
  const [strategy, setStrategy] = useState(
    (cookied?.strategy ?? initial.strategy) || 'Normal'
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when the user has no cookie saved for this battle type yet
  const [unsaved, setUnsaved] = useState(!cookied);

  // Compute CSS --val percentages for the gradient track fill
  // surrenderHP: range [0, 90] → fill% = surrenderHP / 90 * 100
  // readyHP:     range [10,100] → fill% = (readyHP - 10) / 90 * 100
  const surrenderFill = `${(surrenderHP / 90) * 100}%`;
  const readyFill     = `${((readyHP - 10) / 90) * 100}%`;

  const save = async () => {
    const prefs: PreferencesData = {
      surrenderHP: surrenderHP / 100,
      readyHP: readyHP / 100,
      strategy,
    };
    setError(null);
    try {
      await axios.put(
        `/api/team-battle/lobbies/${lobbyId}/participant/${mercenaryId}`,
        prefs,
        { withCredentials: true }
      );
      saveCookiePrefs(mercenaryId, battleType, prefs);
      setUnsaved(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save prefs error:', err);
      setError('Failed to save - try again.');
    }
  };

  return (
    <div className={styles.prefsForm}>

      {unsaved && (
        <div className={styles.unsavedWarning}>
          ⚠ No saved preferences for this battle type yet, using defaults. Adjust and press <strong>Save</strong> before the battle starts.
        </div>
      )}

      <div className={styles.prefRow}>
        <label className={styles.prefLabel}>
          Combat Strategy
        </label>
        <div className={styles.selectWrapper}>
          <select
            className={styles.prefSelect}
            value={strategy}
            onChange={e => setStrategy(e.target.value)}
          >
            <optgroup label="Balanced">
              <option value="Normal">Normal</option>
              <option value="Normal: Light Attacks">Normal: Light Attacks</option>
              <option value="Normal: Heavy Attacks">Normal: Heavy Attacks</option>
            </optgroup>
            <optgroup label="Aggressive">
              <option value="Offensive">Offensive</option>
              <option value="Offensive: Light Attacks">Offensive: Light Attacks</option>
              <option value="Offensive: Heavy Attacks">Offensive: Heavy Attacks</option>
            </optgroup>
            <optgroup label="Cautious">
              <option value="Defensive">Defensive</option>
              <option value="Defensive: Light Attacks">Defensive: Light Attacks</option>
              <option value="Defensive: Heavy Attacks">Defensive: Heavy Attacks</option>
            </optgroup>
            <optgroup label="Reckless">
              <option value="Berserk">Berserk</option>
              <option value="Berserk: Light Attacks">Berserk: Light Attacks</option>
              <option value="Berserk: Heavy Attacks">Berserk: Heavy Attacks</option>
            </optgroup>
          </select>
        </div>
      </div>

      <div className={styles.prefRow}>
        <label className={styles.prefLabel}>
          Surrender below <span className={styles.prefValue}>{surrenderHP}% HP</span>
        </label>
        <input
          type="range"
          className={styles.prefSlider}
          style={{ '--val': surrenderFill } as React.CSSProperties}
          min={0} max={90} step={10}
          value={surrenderHP}
          onChange={e => handleSurrenderChange(+e.target.value)}
        />
        <div className={styles.rangeLabels}>
          <span>Death (0%)</span>
          <span>Cautious (90%)</span>
        </div>
      </div>

      <div className={styles.prefRow}>
        <label className={styles.prefLabel}>
          Ready when HP ≥ <span className={styles.prefValue}>{readyHP}%</span>
        </label>
        <input
          type="range"
          className={styles.prefSlider}
          style={{ '--val': readyFill } as React.CSSProperties}
          min={10} max={100} step={10}
          value={readyHP}
          onChange={e => handleReadyChange(+e.target.value)}
        />
        <div className={styles.rangeLabels}>
          <span>10%</span>
          <span>100%</span>
        </div>
      </div>

      <button className={styles.saveButton} onClick={save}>Save Preferences</button>
      {saved && <div className={styles.savedIndicator}>✓ Saved</div>}
      {error && <div className={styles.errorIndicator}>{error}</div>}
    </div>
  );
};

export default Preferences;

